import React, { useContext, useRef, useEffect } from 'react';
import './Dot2D.css';
import Footer from '../components/Footer';
import { LanguageContext } from '../translations/LanguageContext';

// Minimal interactive "drag the ball, shoot it into the basket" demo.
const BallDemo = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    const s = {
      width: 0,
      height: 0,
      ball: { x: 0, y: 0, dx: 0, dy: 0, r: 12 },
      basket: { x: 0, y: 0, w: 70, h: 120 },
      dragging: false,
      aim: { x: 0, y: 0 },
      scored: false,
      started: false,
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.width = rect.width;
      s.height = rect.height;
      s.basket.x = rect.width - 70;
      s.basket.y = rect.height / 2;
      if (!s.started) {
        s.ball.x = rect.width * 0.42;
        s.ball.y = rect.height / 2;
      }
    };

    const reset = () => {
      s.ball.x = s.width * 0.42;
      s.ball.y = s.height / 2;
      s.ball.dx = 0;
      s.ball.dy = 0;
      s.scored = false;
    };

    const pointerPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e) => {
      const p = pointerPos(e);
      const dx = p.x - s.ball.x;
      const dy = p.y - s.ball.y;
      if (Math.sqrt(dx * dx + dy * dy) < s.ball.r + 22) {
        s.dragging = true;
        s.started = true;
        s.aim = p;
        canvas.setPointerCapture?.(e.pointerId);
      }
    };

    const onMove = (e) => {
      if (!s.dragging) return;
      s.aim = pointerPos(e);
    };

    const onUp = () => {
      if (!s.dragging) return;
      s.dragging = false;
      const power = 0.16;
      s.ball.dx = (s.ball.x - s.aim.x) * power;
      s.ball.dy = (s.ball.y - s.aim.y) * power;
      s.scored = false;
    };

    const step = () => {
      if (!s.dragging) {
        s.ball.x += s.ball.dx;
        s.ball.y += s.ball.dy;
        s.ball.dx *= 0.99;
        s.ball.dy *= 0.99;

        if (s.ball.x - s.ball.r < 0) {
          s.ball.x = s.ball.r;
          s.ball.dx *= -0.6;
        }
        if (s.ball.y - s.ball.r < 0) {
          s.ball.y = s.ball.r;
          s.ball.dy *= -0.6;
        }
        if (s.ball.y + s.ball.r > s.height) {
          s.ball.y = s.height - s.ball.r;
          s.ball.dy *= -0.6;
        }
        if (s.ball.x - s.ball.r > s.width) {
          reset();
        }

        const b = s.basket;
        if (!s.scored && Math.abs(s.ball.x - b.x) < 20 && Math.abs(s.ball.y - b.y) < b.h / 2) {
          s.scored = true;
          s.ball.dx = 0;
          s.ball.dy = 0;
          window.setTimeout(reset, 450);
        }

        if (Math.abs(s.ball.dx) < 0.04 && Math.abs(s.ball.dy) < 0.04) {
          s.ball.dx = 0;
          s.ball.dy = 0;
        }
      }
      draw();
      raf = requestAnimationFrame(step);
    };

    const draw = () => {
      ctx.clearRect(0, 0, s.width, s.height);

      const b = s.basket;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(b.x - b.w / 2, b.y - b.h / 2);
      ctx.lineTo(b.x + b.w / 2, b.y);
      ctx.lineTo(b.x - b.w / 2, b.y + b.h / 2);
      ctx.stroke();

      if (s.dragging) {
        ctx.strokeStyle = 'rgba(0,0,0,0.25)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(s.ball.x, s.ball.y);
        ctx.lineTo(s.aim.x, s.aim.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      ctx.beginPath();
      ctx.arc(s.ball.x, s.ball.y, s.ball.r, 0, Math.PI * 2);
      ctx.fillStyle = '#111';
      ctx.fill();
    };

    resize();
    reset();
    step();

    window.addEventListener('resize', resize);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
    };
  }, []);

  return <canvas ref={canvasRef} className="dot2d-ball-canvas" />;
};

function Dot2D() {
  const { language } = useContext(LanguageContext);
  const t = (en, de) => (language === 'en' ? en : de);

  const meta = [
    { label: 'FOCUS', value: t('UX/UI Case Study', 'UX/UI-Fallstudie') },
    {
      label: 'ROLE',
      value: t(
        'Information architecture, layout system, visual direction',
        'Informationsarchitektur, Layout-System, visuelle Ausrichtung'
      ),
    },
    {
      label: 'TOOLS',
      value: t(
        'Figma • Design system thinking • Content structuring',
        'Figma • Designsystem-Denken • Content-Strukturierung'
      ),
    },
    { label: 'YEAR', value: '2025' },
  ];

  const screens = [
    t('Start screen', 'Startbildschirm'),
    t('All levels', 'Alle Level'),
    t('Level · funnel', 'Level · Trichter'),
    t('Level · triangles', 'Level · Dreiecke'),
    t('Level · hourglass', 'Level · Sanduhr'),
    t('Level · arrows', 'Level · Pfeile'),
    t('Level · spikes', 'Level · Spitzen'),
    t('Level · grid', 'Level · Raster'),
    t('Level · zigzag', 'Level · Zickzack'),
    t('Level · burst', 'Level · Explosion'),
    t('Level · mixed', 'Level · Gemischt'),
    t('Level · finale', 'Level · Finale'),
  ];

  const reviews = [
    {
      title: 'One more try every time',
      date: '5. Dez.',
      user: 'S._ba',
      text:
        'This game is amazing!!! The puzzles are hard but super fun, the levels are really cool, and the sound design and vibration are awesome. I keep telling myself "one more try" and then I play for at least an hour.',
    },
    {
      title: "most frustrating yet fun i've ever had",
      date: '28. Nov.',
      user: 'tarzillas',
      text:
        "honestly, i've never written a review before but this game got me hooked. the game has some frustrating levels that are still enjoyable. the concept is simple, yet the level designs are so intricate that you have to think sometimes to solve them. and lastly, the sound design!!! i can't stress how well done this game is :)))",
    },
  ];

  return (
    <div className="dot2d-page">
      {/* 1. Hero Section — full-width banner with QR overlay */}
      <header className="dot2d-hero">
        <img
          src="/Dot2D/herobackgrounandiphone.png"
          alt="Dot2D on an iPhone home screen"
          className="dot2d-hero-bg"
        />
        <div className="dot2d-hero-overlay">
          <h1>Dot2D</h1>
          <p className="dot2d-hero-subtitle">
            {t(
              'A physics puzzle game — drag, aim, and shoot the ball into the goal.',
              'Ein Physik-Puzzlespiel — ziehen, zielen und den Ball ins Ziel schießen.'
            )}
          </p>
          <img src="/Dot2D/downloadqrcode.png" alt="Download QR code" className="dot2d-hero-qr" />
        </div>
      </header>

      {/* 2. Meta row */}
      <section className="dot2d-section">
        <div className="dot2d-meta">
          {meta.map((m) => (
            <div key={m.label} className="dot2d-meta-cell">
              <span className="dot2d-meta-label">{m.label}</span>
              <span className="dot2d-meta-value">{m.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Interactive shoot section */}
      <section className="dot2d-section">
        <div className="dot2d-shoot">
          <p className="dot2d-shoot-text">
            {t('Drag the ball and shoot it to the basket', 'Zieh den Ball und schieß ihn in den Korb')}
          </p>
          <BallDemo />
        </div>
      </section>

      {/* 4. Gameplay showcase */}
      <section className="dot2d-showcase">
        <img src="/Dot2D/Dot2D.png" alt="Dot2D gameplay — one shot, one destination" className="dot2d-showcase-img" />
      </section>

      {/* 5. Phone screens grid — transitions from white into the dark section */}
      <section className="dot2d-screens">
        <div className="dot2d-container">
          <div className="dot2d-screens-grid">
            {screens.map((label, i) => (
              <img
                key={label}
                src={`/Dot2D/screen${i + 1}.png`}
                alt={label}
                className="dot2d-screen-img"
              />
            ))}
          </div>
        </div>
      </section>

      {/* 6. Dark section: stats, reviews, app card */}
      <section className="dot2d-dark">
        <div className="dot2d-container">
          <div className="dot2d-stats">
            <div className="dot2d-stats-head">
              <h2>{t('+100 Levels', '+100 Level')}</h2>
              <h2>{t('+25 Obstacles', '+25 Hindernisse')}</h2>
              <p className="dot2d-stats-sub">
                {t('Just you, the ball and the goal.', 'Nur du, der Ball und das Ziel.')}
              </p>
            </div>
            <div className="dot2d-stats-copy">
              <p>
                {t(
                  'Every shot is a puzzle, every bounce is an opportunity, and every victory feels earned. Dot2D turns a simple goal into an exciting journey of skill, patience, and discovery. Ready to take your best shot?',
                  'Jeder Schuss ist ein Rätsel, jeder Abpraller eine Chance und jeder Sieg fühlt sich verdient an. Dot2D macht aus einem einfachen Ziel eine spannende Reise voller Geschick, Geduld und Entdeckung. Bereit für deinen besten Schuss?'
                )}
              </p>
              <p>
                {t(
                  "Don't wait — download Dot2D now and test your aim, timing, and strategy. The cup is waiting!",
                  'Warte nicht — lade Dot2D jetzt herunter und teste Zielgenauigkeit, Timing und Strategie. Der Korb wartet!'
                )}
              </p>
            </div>
          </div>

          <div className="dot2d-reviews">
            {reviews.map((r) => (
              <article key={r.title} className="dot2d-review">
                <h3>{r.title}</h3>
                <div className="dot2d-review-meta">
                  <span className="dot2d-stars">★★★★★</span>
                  <span className="dot2d-review-by">
                    {r.date} · {r.user}
                  </span>
                </div>
                <p>{r.text}</p>
              </article>
            ))}
          </div>

          <div className="dot2d-appcard">
            <div className="dot2d-appcard-icon">
              <span className="dot2d-appcard-dot" />
            </div>
            <div className="dot2d-appcard-text">
              <span className="dot2d-appcard-name">Dot2D</span>
              <span className="dot2d-appcard-cat">{t('Casual Games', 'Gelegenheitsspiele')}</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Dot2D;
